import { useState, useEffect } from "react";
import { Select, SelectItem, Button } from "@heroui/react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { PaperPlaneTilt } from "@phosphor-icons/react";

interface EmailPreviewData {
  subject: string;
  html: string;
  mockData: {
    client?: { name: string; email: string };
    appointment?: {
      buildingName: string;
      unitId: string;
      unitAddress: string;
      appointmentTime: string;
      applicationId: number;
    };
    unit?: {
      unitId: string;
      buildingAddress: string;
    };
    candidateName?: string;
    uuid?: string;
  };
}

const STATUSES = [
  { value: "not_interested", label: "Not Interested" },
  { value: "interested", label: "Interested" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "awaiting_visit", label: "Awaiting Visit" },
  { value: "awaiting_interested", label: "Awaiting Interested" },
  { value: "cancelled", label: "Cancelled" },
  { value: "tenant", label: "Tenant" },
  { value: "awaiting_alternate_selection", label: "Awaiting Alternate Selection" }
];

const LANGUAGES = [
  { value: "nl", label: "Nederlands" },
  { value: "fr", label: "Français" },
  { value: "en", label: "English" }
];

const TEMPLATE_TYPES = [
  { value: "status", label: "Status Update Emails" },
  { value: "invitation", label: "Viewing Invitation" }
];

const RentalEmailPreview = () => {
  const [templateType, setTemplateType] = useState("status");
  const [status, setStatus] = useState("interested");
  const [language, setLanguage] = useState("nl");
  const [preview, setPreview] = useState<EmailPreviewData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    loadPreview();
  }, [templateType, status, language]);

  const loadPreview = async () => {
    try {
      setIsLoading(true);

      const url = templateType === "invitation"
        ? `${API_URL}/rental-appointment/email-preview/invitation/${language}`
        : `${API_URL}/rental-appointment/email-preview/status/${status}/${language}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch preview");
      }

      const data = await response.json();
      setPreview(data);
    } catch (error) {
      console.error("Error loading email preview:", error);
      toast.error("Fout bij het laden van preview");
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!preview) {
      toast.error("No email to send");
      return;
    }

    try {
      setIsSending(true);

      const response = await fetch(`${API_URL}/rental-appointment/email-preview/send-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subject: preview.subject,
          html: preview.html,
          templateType: templateType,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send test email");
      }

      const data = await response.json();
      toast.success(`Test email sent to ${data.sentTo}`);
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error("Failed to send test email");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="p-6 w-[800px] mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-primary">
          Rental Appointment Email Templates
        </h1>
        <p className="text-sm text-neutral-secondary-mid mt-1">
          Preview email templates with mock data
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg border border-neutral-primary/10 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Template Type */}
          <Select
            label="Template Type"
            selectedKeys={[templateType]}
            onChange={(e) => setTemplateType(e.target.value)}
            className="w-full"
          >
            {TEMPLATE_TYPES.map((type) => (
              <SelectItem key={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </Select>

          {/* Status (only for status-update templates) */}
          {templateType === "status" && (
            <Select
              label="Status"
              selectedKeys={[status]}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full"
            >
              {STATUSES.map((s) => (
                <SelectItem key={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </Select>
          )}

          {/* Language */}
          <Select
            label="Language"
            selectedKeys={[language]}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full"
          >
            {LANGUAGES.map((lang) => (
              <SelectItem key={lang.value}>
                {lang.label}
              </SelectItem>
            ))}
          </Select>
        </div>
      </div>

      {/* Mock Data Info */}
      {preview && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-sm text-neutral-primary mb-2">
            Mock Data Used:
          </h3>
          <div className="text-xs text-neutral-secondary-mid space-y-1">
            {preview.mockData.client && (
              <p><strong>Client:</strong> {preview.mockData.client.name} ({preview.mockData.client.email})</p>
            )}
            {preview.mockData.candidateName && (
              <p><strong>Candidate:</strong> {preview.mockData.candidateName}</p>
            )}
            {preview.mockData.appointment && (
              <>
                <p><strong>Unit:</strong> {preview.mockData.appointment.unitId} - {preview.mockData.appointment.unitAddress}</p>
                <p><strong>Building:</strong> {preview.mockData.appointment.buildingName}</p>
                {preview.mockData.appointment.appointmentTime && (
                  <p><strong>Appointment:</strong> {new Date(preview.mockData.appointment.appointmentTime).toLocaleString()}</p>
                )}
              </>
            )}
            {preview.mockData.unit && (
              <p><strong>Unit:</strong> {preview.mockData.unit.unitId} - {preview.mockData.unit.buildingAddress}</p>
            )}
            {preview.mockData.uuid && (
              <p><strong>UUID:</strong> {preview.mockData.uuid}</p>
            )}
          </div>
        </div>
      )}

      {/* Subject Line */}
      {preview && (
        <div className="bg-white rounded-lg border border-neutral-primary/10 p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="text-sm text-neutral-secondary-mid mb-1">Subject:</div>
              <div className="font-semibold text-neutral-primary">{preview.subject}</div>
            </div>
            <Button
              color="primary"
              variant="flat"
              startContent={<PaperPlaneTilt size={18} />}
              onPress={sendTestEmail}
              isLoading={isSending}
              isDisabled={!preview || isSending}
            >
              Send Test Email
            </Button>
          </div>
        </div>
      )}

      {/* Email Preview */}
      <div className="bg-white rounded-lg border border-neutral-primary/10 p-4">
        <div className="text-sm text-neutral-secondary-mid mb-3">Email Preview:</div>
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-neutral-secondary-mid">Loading preview...</div>
          </div>
        ) : preview ? (
          <iframe
            srcDoc={preview.html}
            className="w-full border rounded-lg"
            style={{ minHeight: "600px", height: "70vh" }}
            title="Email Preview"
            sandbox="allow-same-origin"
          />
        ) : (
          <div className="flex items-center justify-center h-96">
            <div className="text-neutral-secondary-mid">No preview available</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalEmailPreview;
